import React, { useState, useEffect } from "react";
import socket from "../../config/socket";
import IconButton from "@mui/material/IconButton";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {
  useGetUserNotificationsQuery,
  useUpdateNotificationMutation,
  useMarkAllNotificationsAsReadMutation,
} from "./notificationApiSlice";
import { useNavigate } from "react-router-dom";
import FormatTimeAgo from "../../components/FormatTimeAgo";
import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NOTIFICATION_TITLES } from "../../config/notificationTitles";

const NotificationBell = () => {
  const { t, i18n } = useTranslation(["Header"]);
  const { data: notificationsData, refetch } = useGetUserNotificationsQuery();
  const [updateNotification] = useUpdateNotificationMutation();
  const [markAllNotificationsAsRead] = useMarkAllNotificationsAsReadMutation();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    socket.on("newNotification", () => {
      //console.log("New notification received");
      refetch();
    });
    return () => {
      socket.off("newNotification");
    };
  }, [refetch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickNotification = async (notification) => {
    await updateNotification(notification._id);
    refetch();

    if (notification.link.includes("minar-sidur/bid")) {
      navigate(notification.link, {
        state: { refetch: true },
      });
    } else {
      navigate(notification.link);
    }

    setAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead().unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const unreadCount =
    notificationsData?.ids.filter(
      (id) => !notificationsData.entities[id].isRead
    ).length || 0;

  return (
    <>
      <IconButton
        onClick={handleClick}
        color="primary"
        sx={{
          backgroundColor: "#333",
          padding: "11px",
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            maxWidth: "500px", // Set the maximum width
            maxHeight: "80vh", // Set the maximum height relative to the viewport height
            overflowY: "auto", // Enable scrolling
            "&::-webkit-scrollbar": {
              width: "6px", // Adjust scrollbar width
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.4)", // Scrollbar color
              borderRadius: "2px", // Scrollbar border radius
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 16px",
            borderBottom: "1px solid #e0e0e0", // Line under header
            wordWrap: "break-word", // Ensure long text wraps to the next line
          }}
        >
          <Typography>
            {t("notifications")}
          </Typography>
          {notificationsData?.ids.length > 0 && unreadCount > 0 && (
            <Button 
              size="small" 
              color="primary" 
              onClick={handleMarkAllAsRead}
              sx={{ ml: 2 }}
            >
              {t("clearAll", "Clear all")}
            </Button>
          )}
        </Box>
        {notificationsData?.ids.length > 0 ? (
          notificationsData.ids.map((id) => {
            const notification = notificationsData.entities[id];
            const isRead = notification.isRead;
            return (
              <MenuItem
                key={id}
                onClick={() => handleClickNotification(notification)}
                sx={{
                  backgroundColor: isRead ? "white" : "#D3FAD9",
                  color: "black",
                  "&:hover": {
                    backgroundColor: isRead ? "#78CA72" : "#78CA72",
                  },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  whiteSpace: "normal", // Allows text to wrap
                  overflowWrap: "break-word", // Breaks words to prevent overflow
                }}
              >
                <strong>
                  {NOTIFICATION_TITLES[notification.title]
                    ? t(`notification.${notification.title}`)
                    : notification.title}
                </strong>

                <span>
                  {(() => {
                    // Convert the message to the format in the translation file 
                    // (without % symbols in the key)
                    let translationKey = notification.message.replace(/%/g, "");

                    // Try to get the translated text
                    let translatedText = t(`notification.${translationKey}`);

                    // If translation is missing or has issues, use a fallback approach
                    if (translatedText === translationKey ||
                      translatedText.trim() === translationKey.trim() ||
                      translatedText.trim() === "bidTitle" ||
                      translatedText === ` bidTitle`) {

                      // For new inquiry messages
                      if (translationKey.includes("new inquiry") || translationKey.includes("New inquiry")) {
                        if (i18n.language === 'is') {
                          translatedText = `Ný fyrirspurn barst vegna: %bidTitle%`;
                        } else {
                          translatedText = `A new inquiry was received due to: %bidTitle%`;
                        }
                      }
                      // For response to query messages
                      else if (translationKey.includes("response to a query")) {
                        if (i18n.language === 'is') {
                          translatedText = `Svar við fyrirspurn barst vegna: %bidTitle%`;
                        } else {
                          translatedText = `A response to a query was received due to: %bidTitle%`;
                        }
                      }
                      // For new offer messages
                      else if (translationKey.includes("new offer")) {
                        if (i18n.language === 'is') {
                          translatedText = `Þú fékkst nýtt tilboð í %bidTitle% frá %companyName%`;
                        } else {
                          translatedText = `You received a new offer for %bidTitle% from %companyName%`;
                        }
                      }
                      // For new request messages
                      else if (translationKey.includes("new request")) {
                        if (i18n.language === 'is') {
                          translatedText = `Þú fékkst nýja beiðni um %bidTitle% frá %companyName%`;
                        } else {
                          translatedText = `You received a new request for %bidTitle% from %companyName%`;
                        }
                      }
                      else if (translationKey.includes("new document has been added")) {
                        if (i18n.language === 'is') {
                          translatedText = `Nýtt skjal hefur verið bætt við: %bidTitle%`;
                        } else {
                          translatedText = `A new document has been added to: %bidTitle%`;
                        }
                      }
                      //New tender in industry
                      else if (translationKey.includes("New tender registered in your industry")) {
                        if (i18n.language === 'is') {
                          translatedText = `Nýtt útboð skráð í þinni atvinnugrein: %bidTitle%`;
                        } else {
                          translatedText = `New tender registered in your industry: %bidTitle%`;
                        }
                      }
                      // Invite to closed tender 
                      else if (translationKey.includes("You have been invited to a closed tender")) {
                        if (i18n.language === 'is') {
                          translatedText = `Þér hefur verið boðið í lokað útboð: %bidTitle%`;
                        } else {
                          translatedText = `You have been invited to a closed tender: %bidTitle%`;
                        }
                      }
                      // For offer rejected messages
                      else if (translationKey.includes("offer") && translationKey.includes("rejected")) {
                        if (i18n.language === 'is') {
                          if (translationKey.includes("lotName")) {
                            translatedText = `Tilboði þínu í %lotName% í %bidTitle% hefur verið hafnað`;
                          } else {
                            translatedText = `Tilboð þitt í %bidTitle% hefur verið hafnað`;
                          }
                        } else {
                          if (translationKey.includes("lotName")) {
                            translatedText = `Your offer for %lotName% in %bidTitle% has been rejected`;
                          } else {
                            translatedText = `Your offer in %bidTitle% has been rejected`;
                          }
                        }
                      }
                      // For offer accepted messages
                      else if (translationKey.includes("offer") && translationKey.includes("accepted")) {
                        if (i18n.language === 'is') {
                          if (translationKey.includes("lotName")) {
                            translatedText = `Tilboði þínu í %lotName% í %bidTitle% hefur verið samþykkt`;
                          } else {
                            translatedText = `Tilboð þitt í %bidTitle% hefur verið samþykkt`;
                          }
                        } else {
                          if (translationKey.includes("lotName")) {
                            translatedText = `Your offer for %lotName% in %bidTitle% has been accepted`;
                          } else {
                            translatedText = `Your offer in %bidTitle% has been accepted`;
                          }
                        }
                      }
                      // Default fallback - just use the original message with bidTitle replaced
                      else {
                        translatedText = translationKey.replace("bidTitle", "%bidTitle%")
                          .replace("userName", "%userName%")
                          .replace("companyName", "%companyName%")
                          .replace("lotName", "%lotName%")
                          .replace("productName", "%productName%");
                      }
                    }

                    // Replace the placeholders with actual values
                    const result = translatedText
                      .replace(/%bidTitle%/g, `'${notification.bidTitle}'`)
                      .replace(
                        /%userName%/g,
                        notification.userName ? `'${notification.userName}'` : ""
                      )
                      .replace(
                        /%productName%/g,
                        notification.productName || notification.title || ""
                      )
                      .replace(
                        /%lotName%/g,
                        notification.lotName ? `'${notification.lotName}'` : ""
                      )
                      .replace(
                        /%companyName%/g,
                        notification.companyName
                          ? `'${notification.companyName}'`
                          : ""
                      )
                      .replace(
                        /%senderName%/g,
                        notification.senderName
                          ? `'${notification.senderName}'`
                          : ""
                      )
                      .replace(
                        /%fileName%/g,
                        notification.fileName
                          ? `'${notification.fileName}'`
                          : ""
                      );
                    return result;
                  })()}
                </span>
                <span
                  style={{
                    fontSize: "0.8em",
                    color: "grey",
                    paddingTop: "5px",
                  }}
                >
                  <FormatTimeAgo date={notification.createdAt} />
                </span>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>{t("noNotifications")}</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;

